import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Hash, Loader2, X } from 'lucide-react';
import { expedientesAPI } from '../../api';

const BuscadorExpedienteRapido = () => {
  const navigate = useNavigate();
  const [valor, setValor] = useState('');
  const [resultados, setResultados] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [abierto, setAbierto] = useState(false);
  const [indice, setIndice] = useState(0);
  const [sinResultados, setSinResultados] = useState(false);
  const contenedorRef = useRef(null);
  const timeoutRef = useRef(null);
  const ultimaQuery = useRef('');

  useEffect(() => {
    const fuera = (e) => {
      if (contenedorRef.current && !contenedorRef.current.contains(e.target)) {
        setAbierto(false);
      }
    };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, []);

  const buscar = useCallback(async (texto) => {
    try {
      setCargando(true);
      const res = await expedientesAPI.busquedaRapida(texto, 10);
      const lista = res.data?.resultados || [];
      ultimaQuery.current = texto;
      setResultados(lista);
      setSinResultados(lista.length === 0);
      setIndice(0);
      setAbierto(true);
      return lista;
    } catch (error) {
      setResultados([]);
      setSinResultados(true);
      return [];
    } finally {
      setCargando(false);
    }
  }, []);

  const onChange = (texto) => {
    setValor(texto);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    const limpio = texto.trim();
    if (limpio.length < 1) {
      setResultados([]);
      setSinResultados(false);
      setAbierto(false);
      return;
    }
    timeoutRef.current = setTimeout(() => buscar(limpio), 250);
  };

  const ir = (exp) => {
    setAbierto(false);
    setValor('');
    setResultados([]);
    navigate(`/expedientes/${exp.id}`);
  };

  const limpiar = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setValor('');
    setResultados([]);
    setSinResultados(false);
    setAbierto(false);
  };

  const onKeyDown = async (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAbierto(true);
      setIndice((i) => Math.min(i + 1, Math.max(resultados.length - 1, 0)));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIndice((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Escape') {
      limpiar();
      return;
    }
    if (e.key !== 'Enter') return;

    e.preventDefault();
    const limpio = valor.trim();
    if (!limpio) return;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    // Si el desplegable ya tiene resultados para esta query, usarlos
    let lista = ultimaQuery.current === limpio ? resultados : await buscar(limpio);

    if (lista.length === 1) {
      ir(lista[0]);
    } else if (lista.length > 1) {
      ir(lista[indice] || lista[0]);
    }
  };

  return (
    <div className="relative w-56 lg:w-64" ref={contenedorRef} data-testid="buscador-expediente-rapido">
      <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/70 pointer-events-none" />
      <input
        type="text"
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => resultados.length > 0 && setAbierto(true)}
        placeholder="Nº expediente..."
        title="Búsqueda rápida por número de expediente (Enter para abrir)"
        data-testid="buscador-expediente-rapido-input"
        className="w-full h-9 pl-9 pr-8 rounded-md bg-white/10 border border-white/25 text-sm text-white placeholder:text-white/60 outline-none transition-colors duration-200 focus:bg-white focus:text-gray-900 focus:placeholder:text-gray-400 focus:border-white"
      />
      {cargando && (
        <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-white/70" />
      )}
      {valor && !cargando && (
        <button
          type="button"
          onClick={limpiar}
          data-testid="buscador-expediente-rapido-limpiar"
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/60 hover:text-white focus:text-gray-500"
        >
          <X className="w-4 h-4" />
        </button>
      )}

      {abierto && (resultados.length > 0 || sinResultados) && (
        <div
          className="absolute top-full left-0 mt-1 w-[22rem] bg-white rounded-lg shadow-xl border border-gray-200 max-h-80 overflow-y-auto z-50"
          data-testid="buscador-expediente-rapido-resultados"
        >
          {resultados.length === 0 ? (
            <p className="px-3 py-3 text-sm text-gray-500" data-testid="buscador-expediente-rapido-vacio">
              Ningún expediente con ese número
            </p>
          ) : (
            resultados.map((exp, idx) => (
              <button
                key={exp.id}
                type="button"
                onMouseEnter={() => setIndice(idx)}
                onClick={() => ir(exp)}
                data-testid={`buscador-expediente-rapido-item-${exp.id}`}
                className={`w-full text-left px-3 py-2 border-b last:border-b-0 transition-colors duration-150 ${
                  idx === indice ? 'bg-blue-50' : 'bg-white hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-sm text-blue-700 truncate">{exp.numero_expediente}</span>
                  <span className="text-[10px] uppercase tracking-wide text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded shrink-0">
                    {exp.estado}
                  </span>
                </div>
                <p className="text-xs text-gray-700 truncate">{exp.titulo}</p>
                {exp.cliente_nombre && (
                  <p className="text-[11px] text-gray-400 truncate">{exp.cliente_nombre}</p>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default BuscadorExpedienteRapido;
